import React from 'react';
import { Box, Skeleton, SkeletonProps, TableRow, TableCell, Card, CardContent } from '@mui/material';
import { useDelayedLoading } from '../../hooks/utils/useDelayedLoading';

export interface SkeletonLoaderProps {
    loading: boolean;
    children: React.ReactNode;
    count?: number;
    variant?: SkeletonProps['variant'];
    width?: string | number;
    height?: string | number;
    skeleton?: React.ReactNode;
    sx?: any;
    delay?: number;
}
const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  loading,
  children,
  count = 1,
  variant = 'text',
  width = '100%',
  height,
  skeleton,
  sx,
  delay = 300,
}) => {
  const { showSkeleton } = useDelayedLoading({ loading, delay });
  
  if (showSkeleton) {
    if (skeleton) {
      return <Box sx={sx}>{skeleton}</Box>;
    }

    return (
      <Box sx={sx}>
        {Array.from({ length: count }).map((_, index) => (
          <Skeleton
            key={index}
            variant={variant}
            width={width}
            height={height}
            animation="wave"
            sx={{ 
              mb: index < count - 1 ? 1 : 0,
              borderRadius: variant === 'rectangular' ? 2 : undefined,
              bgcolor: 'rgba(0, 0, 0, 0.08)',
            }}
          />
        ))}
      </Box>
    );
  }

  return <>{children}</>;
};

export default SkeletonLoader;

export interface TableSkeletonColumn {
    variant?: SkeletonProps['variant'];
    width?: string | number;
    height?: string | number;
}

export interface TableSkeletonLoaderProps {
    loading: boolean;
    children: React.ReactNode;
    columns: number | TableSkeletonColumn[];
    rows?: number;
    delay?: number;
}

export const TableSkeletonLoader: React.FC<TableSkeletonLoaderProps> = ({
  loading,
  children,
  columns,
  rows = 5,
  delay = 300,
}) => {
  const { showSkeleton } = useDelayedLoading({ loading, delay });
  
  if (!showSkeleton) {
    return <>{children}</>;
  }

  // If columns is a number, use simple default skeletons
  // If it's an array, use the provided configurations
  const columnCount = typeof columns === 'number' ? columns : columns.length;
  const columnConfigs: TableSkeletonColumn[] = typeof columns === 'number'
    ? Array.from({ length: columnCount }).map(() => ({ variant: 'text' as const, width: '70%' }))
    : columns;

  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <TableRow key={rowIndex}>
          {columnConfigs.map((col, colIndex) => (
            <TableCell key={colIndex}>
              <Skeleton
                variant={col.variant || 'text'}
                width={col.width || '70%'}
                height={col.height}
                animation="wave"
                sx={{
                  ...(col.variant === 'rectangular' ? { borderRadius: 1.5 } : undefined),
                  bgcolor: (theme: any) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                }}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
};

export interface CardSkeletonLoaderProps {
    loading: boolean;
    children: React.ReactNode;
    count?: number;
    showImage?: boolean;
    showTitle?: boolean;
    showDescription?: boolean;
    showActions?: boolean;
    imageHeight?: string | number;
    minHeight?: string | number;
    sx?: any;
    delay?: number;
}

export const CardSkeletonLoader: React.FC<CardSkeletonLoaderProps> = ({
  loading,
  children,
  count = 3,
  showImage = true,
  showTitle = true,
  showDescription = true,
  showActions = false,
  imageHeight = 200,
  minHeight,
  sx,
  delay = 300,
}) => {
  const { showSkeleton } = useDelayedLoading({ loading, delay });
  
  if (!showSkeleton) {
    return <>{children}</>;
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, ...sx }}>
      {Array.from({ length: count }).map((_, index) => (
        <Card key={index} sx={{
          flex: sx?.display === 'grid' ? 'none' : { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)', md: '1 1 calc(33.333% - 22px)' },
          minWidth: sx?.display === 'grid' ? 'auto' : 280,
          width: sx?.display === 'grid' ? '100%' : 'auto',
          minHeight: minHeight || 'auto',
          height: minHeight ? minHeight : 'auto',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          border: '1px solid',
          borderColor: 'border.light',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          overflow: 'hidden',
          fontSize: '4rem'
        }}>
          {showImage && (
            <Skeleton 
              variant="rectangular" 
              width="100%" 
              height={imageHeight}
              animation="wave"
              sx={{ bgcolor: 'rgba(0, 0, 0, 0.08)' }}
            />
          )}
          <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: { xs: 2, sm: 2.5 } }}>
            {showTitle && (
              <Skeleton 
                variant="text" 
                width="80%" 
                height={32} 
                animation="wave"
                sx={{ mb: 1.5, borderRadius: 1, bgcolor: 'rgba(0, 0, 0, 0.08)' }} 
              />
            )}
            {showDescription && (
              <>
                <Skeleton variant="text" width="100%" height={20} animation="wave" sx={{ mb: 0.75, borderRadius: 1, bgcolor: 'rgba(0, 0, 0, 0.08)' }} />
                <Skeleton variant="text" width="90%" height={20} animation="wave" sx={{ mb: 0.75, borderRadius: 1, bgcolor: 'rgba(0, 0, 0, 0.08)' }} />
                <Skeleton variant="text" width="75%" height={20} animation="wave" sx={{ mb: 1.5, borderRadius: 1, bgcolor: 'rgba(0, 0, 0, 0.08)' }} />
                <Skeleton variant="text" width="85%" height={20} animation="wave" sx={{ mb: 0.75, borderRadius: 1, bgcolor: 'rgba(0, 0, 0, 0.08)' }} />
                <Skeleton variant="text" width="70%" height={20} animation="wave" sx={{ borderRadius: 1, bgcolor: 'rgba(0, 0, 0, 0.08)' }} />
              </>
            )}
            {showActions && (
              <Box sx={{ display: 'flex', gap: 1.5, mt: 2.5 }}>
                <Skeleton 
                  variant="rectangular" 
                  width={90} 
                  height={40} 
                  animation="wave"
                  sx={{ borderRadius: 2, bgcolor: 'rgba(0, 0, 0, 0.08)' }} 
                />
                <Skeleton 
                  variant="rectangular" 
                  width={90} 
                  height={40}
                  animation="wave" 
                  sx={{ borderRadius: 2, bgcolor: 'rgba(0, 0, 0, 0.08)' }} 
                />
              </Box>
            )}
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};
